// Top ~60 countries with default currencies
// Keep minimal payload per entry to stay within style/size guidelines.
export interface CountryMeta {
  iso2: string;
  name: string;
  defaultCurrency: string;
}

export const COUNTRIES: readonly CountryMeta[] = [
  { iso2: "US", name: "United States", defaultCurrency: "USD" },
  { iso2: "CA", name: "Canada", defaultCurrency: "CAD" },
  { iso2: "GB", name: "United Kingdom", defaultCurrency: "GBP" },
  { iso2: "AU", name: "Australia", defaultCurrency: "AUD" },
  { iso2: "NZ", name: "New Zealand", defaultCurrency: "NZD" },
  { iso2: "EU", name: "European Union", defaultCurrency: "EUR" },
  { iso2: "DE", name: "Germany", defaultCurrency: "EUR" },
  { iso2: "FR", name: "France", defaultCurrency: "EUR" },
  { iso2: "NL", name: "Netherlands", defaultCurrency: "EUR" },
  { iso2: "BE", name: "Belgium", defaultCurrency: "EUR" },
  { iso2: "LU", name: "Luxembourg", defaultCurrency: "EUR" },
  { iso2: "IE", name: "Ireland", defaultCurrency: "EUR" },
  { iso2: "ES", name: "Spain", defaultCurrency: "EUR" },
  { iso2: "PT", name: "Portugal", defaultCurrency: "EUR" },
  { iso2: "IT", name: "Italy", defaultCurrency: "EUR" },
  { iso2: "AT", name: "Austria", defaultCurrency: "EUR" },
  { iso2: "FI", name: "Finland", defaultCurrency: "EUR" },
  { iso2: "GR", name: "Greece", defaultCurrency: "EUR" },
  { iso2: "CY", name: "Cyprus", defaultCurrency: "EUR" },
  { iso2: "MT", name: "Malta", defaultCurrency: "EUR" },
  { iso2: "EE", name: "Estonia", defaultCurrency: "EUR" },
  { iso2: "LV", name: "Latvia", defaultCurrency: "EUR" },
  { iso2: "LT", name: "Lithuania", defaultCurrency: "EUR" },
  { iso2: "SK", name: "Slovakia", defaultCurrency: "EUR" },
  { iso2: "SI", name: "Slovenia", defaultCurrency: "EUR" },
  { iso2: "PL", name: "Poland", defaultCurrency: "PLN" },
  { iso2: "CZ", name: "Czech Republic", defaultCurrency: "CZK" },
  { iso2: "HU", name: "Hungary", defaultCurrency: "HUF" },
  { iso2: "RO", name: "Romania", defaultCurrency: "RON" },
  { iso2: "BG", name: "Bulgaria", defaultCurrency: "BGN" },
  { iso2: "SE", name: "Sweden", defaultCurrency: "SEK" },
  { iso2: "NO", name: "Norway", defaultCurrency: "NOK" },
  { iso2: "DK", name: "Denmark", defaultCurrency: "DKK" },
  { iso2: "CH", name: "Switzerland", defaultCurrency: "CHF" },
  { iso2: "IS", name: "Iceland", defaultCurrency: "ISK" },
  { iso2: "TR", name: "Turkey", defaultCurrency: "TRY" },
  { iso2: "RU", name: "Russia", defaultCurrency: "RUB" },
  { iso2: "IL", name: "Israel", defaultCurrency: "ILS" },
  { iso2: "AE", name: "United Arab Emirates", defaultCurrency: "AED" },
  { iso2: "SA", name: "Saudi Arabia", defaultCurrency: "SAR" },
  { iso2: "QA", name: "Qatar", defaultCurrency: "QAR" },
  { iso2: "KW", name: "Kuwait", defaultCurrency: "KWD" },
  { iso2: "BH", name: "Bahrain", defaultCurrency: "BHD" },
  { iso2: "OM", name: "Oman", defaultCurrency: "OMR" },
  { iso2: "IN", name: "India", defaultCurrency: "INR" },
  { iso2: "CN", name: "China", defaultCurrency: "CNY" },
  { iso2: "HK", name: "Hong Kong", defaultCurrency: "HKD" },
  { iso2: "TW", name: "Taiwan", defaultCurrency: "TWD" },
  { iso2: "JP", name: "Japan", defaultCurrency: "JPY" },
  { iso2: "KR", name: "South Korea", defaultCurrency: "KRW" },
  { iso2: "SG", name: "Singapore", defaultCurrency: "SGD" },
  { iso2: "MY", name: "Malaysia", defaultCurrency: "MYR" },
  { iso2: "TH", name: "Thailand", defaultCurrency: "THB" },
  { iso2: "VN", name: "Vietnam", defaultCurrency: "VND" },
  { iso2: "ID", name: "Indonesia", defaultCurrency: "IDR" },
  { iso2: "PH", name: "Philippines", defaultCurrency: "PHP" },
  { iso2: "BD", name: "Bangladesh", defaultCurrency: "BDT" },
  { iso2: "PK", name: "Pakistan", defaultCurrency: "PKR" },
  { iso2: "ZA", name: "South Africa", defaultCurrency: "ZAR" },
  { iso2: "NG", name: "Nigeria", defaultCurrency: "NGN" },
  { iso2: "EG", name: "Egypt", defaultCurrency: "EGP" },
  { iso2: "KE", name: "Kenya", defaultCurrency: "KES" },
  { iso2: "MA", name: "Morocco", defaultCurrency: "MAD" },
  { iso2: "GH", name: "Ghana", defaultCurrency: "GHS" },
  { iso2: "BR", name: "Brazil", defaultCurrency: "BRL" },
  { iso2: "AR", name: "Argentina", defaultCurrency: "ARS" },
  { iso2: "CL", name: "Chile", defaultCurrency: "CLP" },
  { iso2: "CO", name: "Colombia", defaultCurrency: "COP" },
  { iso2: "PE", name: "Peru", defaultCurrency: "PEN" },
  { iso2: "MX", name: "Mexico", defaultCurrency: "MXN" },
  { iso2: "UY", name: "Uruguay", defaultCurrency: "UYU" },
];

// Build quick-lookup maps
export const COUNTRIES_BY_ISO2: Record<string, CountryMeta> = Object.fromEntries(
  COUNTRIES.map((c) => [c.iso2.toUpperCase(), c])
);
export const COUNTRIES_BY_NAME: Record<string, CountryMeta> = Object.fromEntries(
  COUNTRIES.map((c) => [c.name.toLowerCase(), c])
);

export function getCountryByIso2(code?: string): CountryMeta | undefined {
  if (!code) return undefined;
  return COUNTRIES_BY_ISO2[code.toUpperCase()];
}

export function getDefaultCurrencyForCountry(countryOrIso2?: string): string | undefined {
  if (!countryOrIso2) return undefined;
  const key = countryOrIso2.trim();
  const byIso = getCountryByIso2(key);
  if (byIso) return byIso.defaultCurrency;
  const byName = COUNTRIES_BY_NAME[key.toLowerCase()];
  return byName?.defaultCurrency;
}
